import React from 'react';
import {Dimensions, Platform, View} from 'react-native';
import {Card, Image, makeStyles} from '@rneui/themed';
import {observer} from 'mobx-react';

import onLight from '../../../images/brand/rewards/on-light.png';
import {AppRoute} from '../../../models/AppRoute';
import useAppNavigation from '../../../navigation/useAppNavigation';
import {useStores} from '../../../stores/use-stores';
import {OffersItemWithMflId} from '../../../transport/types';
import {getAwaitingDealMessage, isCoffeeCardUnearned} from '../../../utils/coffeeCard';
import Text from '../../themed/Text';
import PressableWithOpacityFeedback from '../../ui-controls/PressableWithOpacityFeedback';

const ExclusiveOffersTileCompact = observer((offer: OffersItemWithMflId) => {
  const styles = useStyles();
  const navigation = useAppNavigation();
  const {cartStore} = useStores();

  const isApplied = cartStore.isOfferApplied(offer.mflId);

  /**
   * DIS-1661: an offer with no mflId cannot be applied at all — apply is keyed
   * on the MFL deal id. `canApply` is only honoured when the server also states
   * a `disabledReason`: the middleware hardcodes `canApply: false` with a null
   * reason for every offer today, so honouring it on its own would disable
   * APPLY across the whole app.
   */
  // DIS-1661 Task D3: a fully redeemed coffee card still 403s if applied —
  // isFullyRedeemed reached the client (offer.coffeeCardStats.fully_redeemed)
  // but nothing read it here, so the button stayed live.
  const isFullyRedeemed =
    offer.offerTrigger === 'Coffee Card' && offer.coffeeCardStats.fully_redeemed;

  // DIS-1729: a coffee card is recursive — redeeming it resets the bottles to
  // empty while fully_redeemed stays false (that flag is the card LIMIT, not the
  // cycle), so APPLY went live again on a card the member has not earned.
  const isUnearned = isCoffeeCardUnearned(offer);

  // DIS-1912: an earned card waits for MFL to list the reward deal.
  const awaitingDealMessage = getAwaitingDealMessage(offer);

  const cannotApply =
    !offer.mflId ||
    isFullyRedeemed ||
    isUnearned ||
    !!awaitingDealMessage ||
    (offer.canApply === false && !!offer.disabledReason);

  const handleApply = async () => {
    // Defence in depth behind the disabled APPLY button below — covers no
    // mflId (nothing to apply against) and a fully redeemed coffee card
    // (would 403). The `!offer.mflId` check is also what narrows mflId to
    // a definite string for the call below.
    if (!offer.mflId || cannotApply) {
      return;
    }
    const error = await cartStore.applyOffer(offer.mflId);
    // DIS-1911: a missing store number is fixed by picking a store.
    if (error?.recoveryAction === 'select_store') {
      navigation.navigate(AppRoute.HOME, {openSuburbModal: true});
    }
  };

  const handleRemove = () => {
    cartStore.removeActivatedDeal();
  };

  return (
    <PressableWithOpacityFeedback
      onPress={() => {
        if (offer.offerId) {
          navigation.navigate(AppRoute.EXCLUSIVE_OFFER_DETAIL, {
            offerId: offer.offerId,
          });
        }
      }}>
      <Card
        noMargin
        applied={null}
        variant={'button2'}
        containerStyle={[
          {
            height: 203,
            maxWidth: 270,
            width: Dimensions.get('window').width - 30,
          },
          isApplied && styles.appliedCard,
        ]}
        wrapperStyle={{
          flex: 1,
          gap: 12,
        }}>
        <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
          <Image source={onLight} style={{width: 50, height: 36}} />
          <Text
            style={styles.title}
            numberOfLines={2}
            ellipsizeMode="tail"
            textBreakStrategy="simple"
            lineBreakStrategyIOS="none">
            {offer.tileHeadline}
          </Text>
        </View>
        <Card.Divider
          style={{
            marginBottom: 0,
            borderStyle: Platform.select({web: 'dashed'}),
          }}
        />
        <Text
          style={styles.description}
          numberOfLines={2}
          ellipsizeMode="tail"
          textBreakStrategy="simple"
          lineBreakStrategyIOS="none">
          {offer.description}
        </Text>
        <View style={{flex: 1}} />
        {awaitingDealMessage ? (
          <Text
            style={styles.description}
            numberOfLines={2}
            testID="exclusive-offer-awaiting-deal-message">
            {awaitingDealMessage}
          </Text>
        ) : null}
        {isApplied ? (
          <PressableWithOpacityFeedback
            onPress={e => {
              e?.stopPropagation();
              handleRemove();
            }}
            disabled={cartStore.dirtyOrder}
            style={cartStore.dirtyOrder ? {opacity: 0.5} : undefined}
            testID="exclusive-offer-remove-button">
            <Text style={[styles.apply, styles.remove]} variant="heading3">
              REMOVE
            </Text>
          </PressableWithOpacityFeedback>
        ) : (
          <PressableWithOpacityFeedback
            onPress={e => {
              e?.stopPropagation();
              handleApply();
            }}
            disabled={cartStore.dirtyOrder || cannotApply}
            style={cartStore.dirtyOrder || cannotApply ? {opacity: 0.5} : undefined}
            testID="exclusive-offer-apply-button">
            <Text style={styles.apply} variant="heading3">
              APPLY
            </Text>
          </PressableWithOpacityFeedback>
        )}
      </Card>
    </PressableWithOpacityFeedback>
  );
});

const useStyles = makeStyles(theme => {
  return {
    title: {
      flex: 1,
      fontSize: 14,
      fontWeight: 'bold',
      userSelect: 'none',
    },
    description: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.grey2,
      userSelect: 'none',
    },
    apply: {
      textAlign: 'right',
    },
    applied: {
      color: theme.colors.black,
    },
    remove: {
      color: '#DC2626',
    },
    appliedCard: {
      borderColor: '#F59E0B',
      borderWidth: 2,
    },
  };
});

export default ExclusiveOffersTileCompact;
